import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { generateRandomPage, getLastDigit, isOut } from '../data/mock';
import { matchAPI } from '../services/api';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [gameMode, setGameMode] = useState(null); // 'pvp' or 'pvc'
  const [player1Name, setPlayer1Name] = useState('Player 1');
  const [player2Name, setPlayer2Name] = useState('Player 2');
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [player1Score, setPlayer1Score] = useState(0);
  const [player2Score, setPlayer2Score] = useState(0);
  const [player1Balls, setPlayer1Balls] = useState([]);
  const [player2Balls, setPlayer2Balls] = useState([]);
  const [gameStatus, setGameStatus] = useState('idle'); // idle, playing, finished
  const [currentPage, setCurrentPage] = useState(null);
  const [lastDigit, setLastDigit] = useState(null);
  const [isPlayerOut, setIsPlayerOut] = useState(false);
  const [matchHistory, setMatchHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ total_matches: 0, pvp_matches: 0, pvc_matches: 0, highest_score: 0 });

  // Load match history from backend on mount
  useEffect(() => {
    loadMatchHistory();
    loadStats();
  }, []);

  const loadMatchHistory = async () => {
    try {
      setIsLoading(true);
      const result = await matchAPI.getMatches(50, 0);
      if (result.success) {
        setMatchHistory(result.data.matches || []);
      }
    } catch (error) {
      console.error('Failed to load match history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await matchAPI.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    // Sound effects placeholder
    console.log(`Playing sound: ${type}`);
  }, [soundEnabled]);

  const startGame = useCallback((mode, p1Name, p2Name) => {
    setGameMode(mode);
    setPlayer1Name(p1Name || 'Player 1');
    setPlayer2Name(mode === 'pvc' ? 'Computer' : (p2Name || 'Player 2'));
    setCurrentPlayer(1);
    setPlayer1Score(0);
    setPlayer2Score(0);
    setPlayer1Balls([]);
    setPlayer2Balls([]);
    setGameStatus('playing');
    setCurrentPage(null);
    setLastDigit(null);
    setIsPlayerOut(false);
  }, []);

  // Helper function to finish the game and save to backend
  const finishGame = useCallback(async (p1Score, p2Score, p1Balls, p2Balls) => {
    const winner = p1Score > p2Score 
      ? player1Name 
      : p2Score > p1Score 
        ? player2Name 
        : 'Tie';
    
    const newMatch = {
      mode: gameMode,
      player1: { name: player1Name, score: p1Score },
      player2: { name: player2Name, score: p2Score },
      winner,
      balls: [...p1Balls, ...p2Balls]
    };
    
    // Save to backend
    try {
      const result = await matchAPI.saveMatch(newMatch);
      if (result.success) {
        // Add the saved match with ID to history
        setMatchHistory(prev => [result.data, ...prev]);
        // Reload stats
        loadStats();
      } else {
        // Fallback: add locally with generated ID
        const localMatch = {
          ...newMatch,
          id: Date.now().toString(),
          date: new Date().toISOString()
        };
        setMatchHistory(prev => [localMatch, ...prev]);
      }
    } catch (error) {
      console.error('Failed to save match:', error);
      // Fallback: add locally
      const localMatch = {
        ...newMatch,
        id: Date.now().toString(),
        date: new Date().toISOString()
      };
      setMatchHistory(prev => [localMatch, ...prev]);
    }
    
    setGameStatus('finished');
    playSound('cheer');
  }, [gameMode, player1Name, player2Name, playSound]);

  const flipBook = useCallback(() => {
    if (gameStatus !== 'playing' || isFlipping) return;

    setIsFlipping(true);
    playSound('flip');

    try {
      setTimeout(() => {
        try {
          const page = generateRandomPage();
          const digit = getLastDigit(page);
          const out = isOut(digit);

          setCurrentPage(page);
          setLastDigit(digit);
          setIsPlayerOut(out);

          if (currentPlayer === 1) {
            if (!out) {
              setPlayer1Score(prev => prev + digit);
              setPlayer1Balls(prev => [...prev, digit]);
              playSound('hit');
            } else {
              setPlayer1Balls(prev => [...prev, digit]);
              playSound('out');
            }
          } else {
            // Player 2's turn - check chase conditions
            if (!out) {
              const newScore = player2Score + digit;
              setPlayer2Score(newScore);
              setPlayer2Balls(prev => [...prev, digit]);
              playSound('hit');
              
              // Check if Player 2 has beaten Player 1's score
              if (newScore > player1Score) {
                // Player 2 wins immediately!
                setTimeout(() => {
                  finishGame(player1Score, newScore, player1Balls, [...player2Balls, digit]);
                }, 500);
              }
            } else {
              // Player 2 is OUT
              setPlayer2Balls(prev => [...prev, digit]);
              playSound('out');
              
              // Game ends - compare scores
              setTimeout(() => {
                finishGame(player1Score, player2Score, player1Balls, [...player2Balls, digit]);
              }, 500);
            }
          }

          setIsFlipping(false);
        } catch (innerError) {
          console.error('Error during flip animation:', innerError);
          setIsFlipping(false);
        }
      }, 800);
    } catch (error) {
      console.error('Error initiating flip:', error);
      setIsFlipping(false);
    }
  }, [gameStatus, isFlipping, currentPlayer, playSound, player1Score, player2Score, player1Balls, player2Balls, finishGame]);

  const computerPlay = useCallback(() => {
    if (gameMode !== 'pvc' || currentPlayer !== 2 || gameStatus !== 'playing') return;
    
    setTimeout(() => {
      flipBook();
    }, 1500);
  }, [gameMode, currentPlayer, gameStatus, flipBook]);

  const nextPlayer = useCallback(() => {
    if (currentPlayer === 1) {
      setCurrentPlayer(2);
      setCurrentPage(null);
      setLastDigit(null);
      setIsPlayerOut(false);
    } else {
      // This shouldn't be called for player 2 with new chase logic
      // but keeping for backwards compatibility
      finishGame(player1Score, player2Score, player1Balls, player2Balls);
    }
  }, [currentPlayer, player1Score, player2Score, player1Balls, player2Balls, finishGame]);

  const resetGame = useCallback(() => {
    setGameMode(null);
    setCurrentPlayer(1);
    setPlayer1Score(0);
    setPlayer2Score(0);
    setPlayer1Balls([]);
    setPlayer2Balls([]);
    setGameStatus('idle');
    setCurrentPage(null);
    setLastDigit(null);
    setIsPlayerOut(false);
  }, []);

  const deleteMatch = useCallback(async (matchId) => {
    try {
      const result = await matchAPI.deleteMatch(matchId);
      if (result.success) {
        setMatchHistory(prev => prev.filter(m => m.id !== matchId));
        loadStats();
      }
      return result.success;
    } catch (error) {
      console.error('Failed to delete match:', error);
      return false;
    }
  }, []);

  const value = {
    gameMode,
    player1Name,
    player2Name,
    currentPlayer,
    player1Score,
    player2Score,
    player1Balls,
    player2Balls,
    gameStatus,
    currentPage,
    lastDigit,
    isPlayerOut,
    matchHistory,
    soundEnabled,
    isFlipping,
    isLoading,
    stats,
    setSoundEnabled,
    startGame,
    flipBook,
    nextPlayer,
    resetGame,
    computerPlay,
    deleteMatch,
    loadMatchHistory,
    loadStats
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};
